import React, { useState, Children } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, StarIcon, PlayCircleIcon, CheckIcon, ServerIcon, InfinityIcon, ReceiptIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { usePricing } from '../contexts/PricingContext';
import { ContactFormPopup } from './ContactFormPopup';
export function Hero() {
  const {
    t
  } = useLanguage();
  const {
    price
  } = usePricing();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const container = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  const wordItem = {
    hidden: {
      opacity: 0,
      y: 20,
      filter: 'blur(8px)'
    },
    show: {
      opacity: 1,
      y: 0,
      filter: 'blur(0px)',
      transition: {
        duration: 0.8,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <>
      <section className="relative min-h-screen w-full overflow-hidden pt-32 pb-20 flex items-center" style={{
      backgroundColor: 'var(--bg-primary)'
    }} data-id="element-206">
        {/* Background Glow */}
        <div className="absolute top-1/4 left-0 w-[800px] h-[600px] opacity-30 pointer-events-none" style={{
        background: 'radial-gradient(ellipse at center, var(--accent) 0%, transparent 70%)'
      }} data-id="element-207" />

        <div className="relative z-10 mx-auto flex w-full max-w-7xl flex-col lg:flex-row items-center justify-between gap-12 px-4" data-id="element-208">
          {/* Left Column - Text & CTAs */}
          <div className="w-full lg:w-1/2 text-left flex flex-col items-start" data-id="element-209">
            {/* Badge */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.5
          }} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border mb-4 md:mb-6 bg-white/50 backdrop-blur-sm shadow-sm" style={{
            borderColor: 'var(--border)'
          }} data-id="element-210">
              <span className="text-xs md:text-sm font-semibold tracking-wide text-gray-800 flex items-center gap-1.5" data-id="element-211">
                🔥 {t('hero.badge')}
              </span>
            </motion.div>

            {/* Title */}
            <motion.h1 variants={container} initial="hidden" animate="show" className="mb-6 md:mb-8 text-4xl leading-tight tracking-tight md:text-5xl lg:text-[3.5rem] font-bold max-w-2xl" style={{
            color: 'var(--text-primary)'
          }} data-id="element-212">
              <motion.span variants={wordItem} className="block mb-1" data-id="element-213">
                {t('hero.title').replace('бесплатно.', '').replace('безкоштовно.', '').replace('for free.', '')}
              </motion.span>
              <motion.span variants={wordItem} className="relative inline-block mb-2" data-id="element-214">
                <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 uppercase tracking-wider font-black" data-id="element-215">
                  {t('hero.title').includes('бесплатно') ? 'БЕСПЛАТНО.' : t('hero.title').includes('безкоштовно') ? 'БЕЗКОШТОВНО.' : 'FOR FREE.'}
                </span>
                {/* Marker highlight effect */}
                <motion.div initial={{
                scaleX: 0
              }} animate={{
                scaleX: 1
              }} transition={{
                delay: 1,
                duration: 0.8,
                ease: 'easeOut'
              }} className="absolute bottom-2 left-0 w-full h-4 bg-blue-200/50 -rotate-2 skew-x-12 origin-left" data-id="element-216" />
              </motion.span>
              <br data-id="element-217" />
              <motion.span variants={wordItem} className="inline-block" data-id="element-218">
                {t('hero.titleHighlight').replace('$15/мес.', '').replace('$15/міс.', '').replace('$15/mo.', '')}{' '}
                <span style={{
                color: 'var(--accent)'
              }} data-id="element-219">
                  {price}/мес.
                </span>
              </motion.span>
            </motion.h1>

            {/* Subtitle */}
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.4
          }} className="text-base sm:text-lg md:text-xl mb-6 max-w-xl text-gray-600 font-medium" data-id="element-220">
              {t('hero.subtitle')}
            </motion.p>

            {/* Value Pills */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.6
          }} className="flex flex-wrap gap-3 mb-10" data-id="element-221">
              <div className="flex items-center gap-1.5 bg-green-50 text-green-700 px-3 py-1.5 rounded-lg text-sm font-semibold border border-green-100 shadow-sm" data-id="element-222">
                <CheckIcon className="w-4 h-4" data-id="element-223" /> {t('hero.pill.dev')}
              </div>
              <div className="flex items-center gap-1.5 bg-blue-50 text-blue-700 px-3 py-1.5 rounded-lg text-sm font-semibold border border-blue-100 shadow-sm" data-id="element-224">
                <ServerIcon className="w-4 h-4" data-id="element-225" /> {t('hero.pill.host')}
              </div>
              <div className="flex items-center gap-1.5 bg-purple-50 text-purple-700 px-3 py-1.5 rounded-lg text-sm font-semibold border border-purple-100 shadow-sm" data-id="element-226">
                <InfinityIcon className="w-4 h-4" data-id="element-227" /> {t('hero.pill.edit')}
              </div>
            </motion.div>

            {/* CTA Buttons */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.8
          }} className="flex flex-col sm:flex-row gap-4 items-center justify-start mb-12 w-full sm:w-auto" data-id="element-228">
              <button onClick={() => setIsPopupOpen(true)} className="group relative w-full sm:w-auto flex items-center justify-center gap-2 rounded-2xl px-8 py-4 font-bold text-lg transition-all hover:scale-105 overflow-hidden" style={{
              backgroundColor: 'var(--accent)',
              color: '#FFFFFF'
            }} data-id="element-229">
                {/* Button Glow Effect */}
                <div className="absolute inset-0 w-full h-full bg-white/20 group-hover:translate-x-full -translate-x-full transition-transform duration-500 skew-x-12" data-id="element-230" />
                {t('hero.cta')}
                <ArrowRightIcon className="w-5 h-5 transition-transform group-hover:translate-x-1" data-id="element-231" />
              </button>

              <a href="#how-it-works" className="flex items-center justify-center gap-2 px-8 py-4 rounded-2xl font-semibold text-gray-700 hover:bg-gray-100 transition-colors w-full sm:w-auto" data-id="element-232">
                <PlayCircleIcon className="w-5 h-5" data-id="element-233" />
                {t('nav.howItWorks')}
              </a>
            </motion.div>

            {/* Social Proof */}
            <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 1,
            delay: 1
          }} className="flex items-center gap-3" data-id="element-234">
              <div className="flex gap-1" data-id="element-235">
                {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-5 h-5 text-yellow-400 fill-current" data-id="element-236" />)}
              </div>
              <span className="text-sm font-medium text-gray-600" data-id="element-237">
                {t('stats.clients')} 4.9/5
              </span>
            </motion.div>
          </div>

          {/* Right Column - Video */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.4
        }} className="w-full lg:w-1/2 relative mt-12 lg:mt-0 flex items-center justify-center" data-id="element-238">
            <div className="relative w-full max-w-2xl mx-auto rounded-2xl overflow-hidden shadow-2xl border border-[var(--border)]" data-id="element-239">
              <video src="https://cdn.magicpatterns.com/uploads/vrXe21wkVqS62d5BzpZ9eJ/original-2de319f4767c5a0d4acf4f107b938504.mp4" autoPlay loop muted playsInline className="w-full h-auto" data-id="element-240" />
            </div>

            {/* Floating Invoice Card (The WOW Factor) */}
            <motion.div initial={{
            opacity: 0,
            x: -30,
            y: 30
          }} animate={{
            opacity: 1,
            x: 0,
            y: [0, -10, 0]
          }} transition={{
            opacity: {
              delay: 1.2,
              duration: 0.8
            },
            x: {
              delay: 1.2,
              duration: 0.8,
              type: 'spring',
              stiffness: 100
            },
            y: {
              delay: 2,
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut'
            }
          }} className="absolute -bottom-10 -left-6 sm:-left-12 bg-white/95 backdrop-blur-xl p-5 rounded-2xl shadow-2xl border border-gray-100 w-72 hidden sm:block z-20" data-id="element-241">
              {/* Save Badge */}
              <div className="absolute -top-3 -right-3 bg-red-500 text-white text-xs font-black px-3 py-1 rounded-full shadow-lg transform rotate-12" data-id="element-242">
                {t('hero.receipt.save')}
              </div>

              <div className="flex items-center gap-2 mb-3 border-b border-gray-100 pb-2" data-id="element-243">
                <ReceiptIcon className="w-4 h-4 text-gray-400" data-id="element-244" />
                <span className="text-xs font-bold text-gray-500 uppercase tracking-wider" data-id="element-245">
                  {t('hero.receipt.title')}
                </span>
              </div>
              <div className="space-y-3 mb-4" data-id="element-246">
                <div className="flex justify-between items-center text-sm" data-id="element-247">
                  <span className="text-gray-600 font-medium" data-id="element-248">
                    {t('hero.receipt.design')}
                  </span>
                  <div className="flex items-center gap-2" data-id="element-249">
                    <span className="text-gray-400 line-through text-xs" data-id="element-250">
                      $300
                    </span>
                    <span className="font-bold text-green-500 bg-green-50 px-2 py-0.5 rounded" data-id="element-251">
                      $0
                    </span>
                  </div>
                </div>
                <div className="flex justify-between items-center text-sm" data-id="element-252">
                  <span className="text-gray-600 font-medium" data-id="element-253">
                    {t('hero.receipt.dev')}
                  </span>
                  <div className="flex items-center gap-2" data-id="element-254">
                    <span className="text-gray-400 line-through text-xs" data-id="element-255">
                      $500
                    </span>
                    <span className="font-bold text-green-500 bg-green-50 px-2 py-0.5 rounded" data-id="element-256">
                      $0
                    </span>
                  </div>
                </div>
                <div className="flex justify-between items-center text-sm" data-id="element-257">
                  <span className="text-gray-600 font-medium" data-id="element-258">
                    {t('hero.receipt.support')}
                  </span>
                  <span className="font-bold text-gray-900" data-id="element-259">{price}/мес</span>
                </div>
              </div>
              <div className="border-t border-gray-100 pt-3 flex justify-between items-center bg-gray-50 -mx-5 -mb-5 p-5 rounded-b-2xl" data-id="element-260">
                <span className="text-sm font-bold text-gray-900" data-id="element-261">
                  {t('hero.receipt.total')}
                </span>
                <span className="text-2xl font-black text-[var(--accent)]" data-id="element-262">
                  {price}
                </span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <ContactFormPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} data-id="element-263" />
    </>;
}